import * as Location from "expo-location";
import * as Sentry from "@sentry/react-native";
import analytics from "@react-native-firebase/analytics";
import { Alert, Linking } from "react-native";

import { AppUserSession } from "@/schemas/auth/session";
import ProfileService from "@/services/user/profile";

// Request permission with alert to open settings if denied
export async function requestForegroundLocationPermission(
  alertTitle = "Location Access Needed",
  alertMessage = "Location access is needed to improve your experience. You can enable it in your device settings.",
): Promise<boolean> {
  try {
    const { status } = await Location.requestForegroundPermissionsAsync();

    if (status === Location.PermissionStatus.GRANTED) {
      await analytics().logEvent("app_location_enabled");
      return true;
    }

    await analytics().logEvent("app_location_disabled");

    Alert.alert(alertTitle, alertMessage, [
      { style: "cancel", text: "Not Now" },
      {
        onPress: () => Linking.openSettings(),
        text: "Open Settings",
      },
    ]);

    return false;
  } catch (error) {
    Sentry.captureException(error);
    return false;
  }
}

// Check permission status
export async function hasLocationPermission(): Promise<boolean> {
  try {
    const { status } = await Location.getForegroundPermissionsAsync();
    return status === Location.PermissionStatus.GRANTED;
  } catch (error) {
    Sentry.captureException(error);
    return false;
  }
}

// Register location (assumes permission already granted)
export async function registerUserLocation(
  session: AppUserSession,
): Promise<void> {
  const profileService = new ProfileService(session);
  try {
    const location = await Location.getCurrentPositionAsync({});
    await profileService.registerGPSCoordinates(location);
  } catch (error) {
    Sentry.captureException(error);
  }
}
